import React from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { entrarYVolver } from '../../lib/dominios';
import { ROL_PRESENCIA } from './presencia';

/*
 * Avatares de quienes tienen el documento abierto, en la cabecera del editor (como Google Docs):
 * con cuenta, iniciales; sin cuenta, el emoji de su animal mitológico. Al pasar encima, quién es y qué
 * puede hacer; si no caben, "+N" abre la lista completa.
 */

function iniciales(nombre = '') {
  const partes = nombre.replace(/[^\p{L}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  return ((partes[0]?.[0] || '') + (partes[1]?.[0] || '')).toUpperCase() || '?';
}

export function Avatar({ persona, tamano = 32, anillo = true }) {
  const invitado = persona.tipo === 'invitado';
  return (
    <span
      className={`zo-pres-avatar ${invitado ? 'is-invitado' : ''} ${anillo ? 'con-anillo' : ''}`}
      style={{ '--zo-pres-color': persona.color || '#7f8497', width: tamano, height: tamano, fontSize: invitado ? tamano * 0.56 : tamano * 0.38 }}
      aria-hidden="true"
    >
      {invitado ? persona.emoji : iniciales(persona.nombre)}
    </span>
  );
}

function Ficha({ persona, esYo = false }) {
  const invitado = persona.tipo === 'invitado';
  return (
    <div className="zo-pres-ficha">
      <Avatar persona={persona} tamano={40} anillo={false} />
      <div className="zo-pres-ficha-texto">
        <div className="zo-pres-nombre">
          {persona.nombre}
          {esYo && <span className="zo-pres-tu"> (tú)</span>}
        </div>
        {persona.correo && <div className="zo-pres-correo">{persona.correo}</div>}
        <div className="zo-pres-rol">
          {ROL_PRESENCIA[persona.rol] || 'Puede ver'}
          {invitado && ' · sin cuenta, entró con el enlace'}
        </div>
      </div>
    </div>
  );
}

function AvatarConFicha({ persona }) {
  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button type="button" className="zo-pres-boton" aria-label={`${persona.nombre} · ${ROL_PRESENCIA[persona.rol] || ''}`}>
          <Avatar persona={persona} />
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="zo-pres-tarjeta">
        <Ficha persona={persona} />
      </HoverCardContent>
    </HoverCard>
  );
}

/** Los demás que están en el documento. `max` avatares y el resto en "+N". */
export default function AvataresPresencia({ presentes = [], yo = null, max = 4 }) {
  const otros = presentes.filter((p) => p.id !== yo?.id);
  if (!otros.length) return null;
  const visibles = otros.slice(0, max);
  const resto = otros.length - visibles.length;
  const todos = yo ? [yo, ...otros] : otros;

  return (
    <div className="zo-presencia" role="group" aria-label={`${otros.length} ${otros.length === 1 ? 'persona más tiene' : 'personas más tienen'} abierto el documento`}>
      {visibles.map((p) => <AvatarConFicha key={p.id} persona={p} />)}
      {resto > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="zo-pres-boton zo-pres-mas" aria-label={`Ver a las ${otros.length} personas`}>
              +{resto}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="zo-pres-lista">
            <div className="zo-pres-lista-titulo">En este documento ahora</div>
            {todos.map((p) => <Ficha key={p.id} persona={p} esYo={p.id === yo?.id} />)}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

/** Quien entró sin cuenta ve con qué animal lo ven los demás, y puede entrar con su cuenta. */
export function MiAnimal({ yo }) {
  if (!yo || yo.tipo !== 'invitado') return null;
  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button type="button" className="zo-pres-boton zo-pres-yo" aria-label={`Estás como ${yo.nombre}`}>
          <Avatar persona={yo} tamano={34} />
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="zo-pres-tarjeta">
        <Ficha persona={yo} esYo />
        <p className="zo-pres-nota">Los demás te ven como <strong>{yo.nombre}</strong>. Entra con tu cuenta para aparecer con tu nombre.</p>
        <button type="button" className="zo-pres-entrar" onClick={() => entrarYVolver()}>Iniciar sesión</button>
      </HoverCardContent>
    </HoverCard>
  );
}
